import { FieldValues, Path, useFormContext, useWatch } from 'react-hook-form';
import { FieldConfig } from '../types';
import {
    CheckboxField,
    ComboboxField,
    DigitField,
    SelectField,
    TagsField,
    TextAreaField,
    TextField,
} from './bricks';
import { ArrayField } from './bricks/ArrayField';
import { EmailTagsField } from './bricks/EmailTagsField';

const FieldDispatcher = <TFormValues extends FieldValues>({
    config,
}: {
    config: FieldConfig<TFormValues>;
}) => {
    switch (config.type) {
        case 'text':
        case 'date':
            return <TextField config={config} />;
        case 'textarea':
            return <TextAreaField config={config} />;
        case 'select':
            return <SelectField config={config} />;
        case 'combobox':
            return <ComboboxField config={config} />;
        case 'checkbox':
            return <CheckboxField config={config} />;
        case 'digit':
            return <DigitField config={config} />;
        case 'tags':
            return <TagsField config={config} />;
        case 'emailTags':
            return <EmailTagsField config={config} />;
        case 'array':
            return <ArrayField config={config} />;
        default:
            console.warn(
                `Unsupported field type: ${(config as FieldConfig).type}`,
            );
            return null;
    }
};

const DependentFieldWrapper = <TFormValues extends FieldValues>({
    config,
}: {
    config: FieldConfig<TFormValues>;
}) => {
    const { control } = useFormContext<TFormValues>();

    const watchedValue = useWatch({
        control,
        name: config.dependsOn!.fieldName as Path<TFormValues>,
        exact: true,
    });

    const expectedValue = config.dependsOn!.value;

    const isMatch = Array.isArray(expectedValue)
        ? (expectedValue as unknown[]).includes(watchedValue)
        : watchedValue === expectedValue;

    if (!isMatch) return null;

    return <FieldDispatcher config={config} />;
};

export const FieldRenderer = <TFormValues extends FieldValues>({
    config,
}: {
    config: FieldConfig<TFormValues>;
}) => {
    if (config.dependsOn) return <DependentFieldWrapper config={config} />;

    return <FieldDispatcher config={config} />;
};
